import { useState, useEffect } from "react";
import { Box, Paper, Typography, alpha } from "@mui/material";

import styles from "./ElementInspector.module.scss";

interface ElementInfo {
  tagName: string;
  id: string;
  classList: string[];
}

function ElementInspector(): JSX.Element {
  const [hoveredElement, setHoveredElement] = useState<HTMLElement | null>(null);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [elementInfo, setElementInfo] = useState<ElementInfo | null>(null);

  useEffect(() => {
    if (!isActive) return;

    const handleMouseOver = (event: MouseEvent): void => {
      // Prevent highlighting the inspector itself
      if ((event.target as HTMLElement).closest(`.${styles.container}`)) {
        return;
      }

      event.stopPropagation();
      const target = event.target as HTMLElement;
      setHoveredElement(target);

      setElementInfo({
        tagName: target.tagName.toLowerCase(),
        id: target.id,
        classList: Array.from(target.classList),
      });
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      // Toggle inspector with Shift+I
      if (event.shiftKey && event.key === "I") {
        setIsActive((prevActive) => !prevActive);
      }

      // Escape key to deactivate
      if (event.key === "Escape") {
        setIsActive(false);
      }
    };

    document.addEventListener("mouseover", handleMouseOver, true);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mouseover", handleMouseOver, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const renderOverlay = (): JSX.Element | null => {
    if (!hoveredElement || !isActive || !elementInfo) return null;

    const rect = hoveredElement.getBoundingClientRect();

    return (
      <Box className={styles.overlay}>
        <Box
          sx={{
            position: "fixed",
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            pointerEvents: "none",
            border: "2px solid",
            borderColor: "primary.main",
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
            zIndex: 9999,
          }}
        />
        <Paper
          elevation={3}
          className={styles.infoPanel}
          sx={{
            position: "fixed",
            top: rect.bottom + 5,
            left: rect.left,
            zIndex: 10000,
            pointerEvents: "none",
            backgroundColor: "background.paper",
            p: 1,
          }}
        >
          <Typography
            variant="body2"
            fontFamily="monospace"
            fontWeight="bold"
          >
            {elementInfo.tagName}
            {elementInfo.id ? `#${elementInfo.id}` : ""}
          </Typography>
          {elementInfo.classList.length > 0 && (
            <Typography
              variant="caption"
              color="primary"
              fontFamily="monospace"
              className={styles.classes}
            >
              {elementInfo.classList.map((c) => `.${c}`).join(" ")}
            </Typography>
          )}
        </Paper>
      </Box>
    );
  };

  return <>{renderOverlay()}</>;
}

export default ElementInspector;
